/**
 * Pure formatters + label helpers for the Production Records page.
 *
 * Anything that touches React stays in the .tsx files; the strings
 * + small enum-to-label maps live here so they're trivially
 * unit-testable without an Apollo provider, JSDOM, or React Testing
 * Library. The page tests import these directly.
 */

import type { BadgeProps } from '@ordo/ui/badge'

import type { ProductionLineStatus, PRStatus, TempBand, TempCheckpoint, TempLogStatus } from '@/api/production-records'

// Mealtime display labels come from the app-wide canonical source.
export { MEALTIME_LABELS as MEALTIME_LABEL } from '@/lib/mealtime'

// ---------------------------------------------------------------------------
// Enum labels
// ---------------------------------------------------------------------------

export const PR_STATUS_LABEL: Record<PRStatus, string> = {
  DRAFT: 'Draft',
  // The materializer's header state — the record exists but nobody has
  // worked it yet. "Automated" read as gibberish to schools (PROD feedback);
  // say what it means.
  AUTOMATED: 'Not started',
  REVIEWED: 'Reviewed',
  SUBMITTED: 'Submitted',
  AMENDED: 'Amended',
}

/**
 * The Status every EXPORT of a record prints — the browser print/PDF
 * (`print-document.tsx`) and the school-print CSV (`lib/school-print/csv.ts`).
 * An amended record exports as SIGNED: the export shares only the final
 * signature, never the fact or history of an amendment, so AMENDED reads
 * exactly like SUBMITTED. Every other status keeps its normal label; an
 * unknown one passes through. The on-screen record keeps `PR_STATUS_LABEL`.
 * The server PDF mirrors this in `production-record-document-html.ts`.
 */
export function exportStatusLabel(status: string): string {
  const printed = status === 'AMENDED' ? 'SUBMITTED' : status
  return printed in PR_STATUS_LABEL ? PR_STATUS_LABEL[printed as PRStatus] : status
}

export const TEMP_CHECKPOINT_LABEL: Record<TempCheckpoint, string> = {
  FACILITY: 'Facility hot/cold hold',
  DELIVERY: 'Delivery receipt',
  SERVICE: 'On the serving line',
}

export const TEMP_CHECKPOINT_SHORT_LABEL: Record<TempCheckpoint, string> = {
  FACILITY: 'Facility',
  DELIVERY: 'Delivery',
  SERVICE: 'Service',
}

export const TEMP_LOG_STATUS_LABEL: Record<TempLogStatus, string> = {
  WITHIN_RANGE: 'Within range',
  OUT_OF_RANGE: 'Out of range',
  NOT_RECORDED: 'Not recorded',
}

export const PRODUCTION_LINE_STATUS_LABEL: Record<ProductionLineStatus, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARED: 'Prepared',
  PACKED: 'Packed',
  RECEIVED: 'Received',
  SERVED: 'Served',
  VOIDED: 'Voided',
}

/** Handout method chip labels — shared by the record page and the print views. */
export const HANDOUT_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Individual servings',
  BULK_TRAY: 'Bulk trays',
}

/** Delivery method chip labels — shared by the record page and the print views. */
export const DELIVERY_MODE_LABEL: Record<string, string> = {
  CAFETERIA: 'Cafeteria delivery',
  CLASSROOM: 'Classroom delivery',
}

/**
 * The required HACCP holding band per temperature class, rendered next to
 * every reading so "47.5°F Out of range" is self-explanatory.
 */
export const TEMP_BAND_LABEL: Record<TempBand, string> = {
  HOT: 'Hot · hold ≥ 135°F',
  CHILLED: 'Chilled · hold ≤ 41°F',
  AMBIENT: 'Shelf-stable · exempt',
}

/** Typed printed names render signature-style on the rule. */
export const SIGNATURE_FONT_STACK = "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', cursive"

// ---------------------------------------------------------------------------
// Status badge variants — drives the colored chip on each row.
// ---------------------------------------------------------------------------

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info'

export type BadgeVariant = NonNullable<BadgeProps['variant']>

/** BadgeTone → the real Badge variant (the old list collapsed success to solid-primary). */
export function badgeVariantForTone(tone: BadgeTone): BadgeVariant {
  switch (tone) {
    case 'neutral':
      return 'secondary'
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    case 'destructive':
      return 'destructive'
    case 'info':
      return 'info'
  }
}

/**
 * Map a PRStatus to the visual tone of its badge. Pure — exercised by
 * the page unit tests so any future status added gets explicit
 * coverage rather than falling silently into the neutral default.
 */
export function toneForPRStatus(status: PRStatus): BadgeTone {
  switch (status) {
    case 'DRAFT':
      return 'neutral'
    case 'AUTOMATED':
      return 'neutral'
    case 'REVIEWED':
      return 'success'
    case 'SUBMITTED':
      return 'success'
    case 'AMENDED':
      return 'warning'
  }
}

/**
 * Map a TempLogStatus to its visual tone (the printable report + the
 * detail drawer both render the same chip styling).
 */
export function toneForTempLogStatus(status: TempLogStatus): BadgeTone {
  switch (status) {
    case 'WITHIN_RANGE':
      return 'success'
    case 'OUT_OF_RANGE':
      return 'destructive'
    case 'NOT_RECORDED':
      return 'warning'
  }
}

// ---------------------------------------------------------------------------
// Date / time formatters
// ---------------------------------------------------------------------------

/**
 * Format a YYYY-MM-DD string as a localized full date. `yyyyMmDd` is
 * parsed as local-day to avoid timezone-shift surprises at midnight
 * boundary. Returns the input verbatim if it doesn't parse.
 */
export function formatServiceDate(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map((p) => Number(p))
  if (!y || !m || !d) {
    return yyyyMmDd
  }
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format an ISO timestamp as a relative phrase ("today", "yesterday",
 * "3 days ago") with a fallback to MMM d for older dates. `now` is
 * injected so tests can pin a deterministic clock.
 */
export function formatRelativeDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  const ms = d.getTime() - now.getTime()
  const days = Math.round(ms / (1000 * 60 * 60 * 24))
  if (days === 0) {
    return 'today'
  }
  if (days === -1) {
    return 'yesterday'
  }
  if (days < -1 && days > -14) {
    return `${Math.abs(days)} days ago`
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// ---------------------------------------------------------------------------
// Print-view formatting
// ---------------------------------------------------------------------------

/**
 * Decimals come off Prisma as strings (Decimal.js shape). The page +
 * the printable view both want a clean "104.5°F" display. Returns the
 * input verbatim on a non-numeric value rather than throwing — the
 * page degrades gracefully on bad data.
 */
export function formatTemperature(value: string, unit: string = 'F'): string {
  const num = Number(value)
  if (Number.isNaN(num)) {
    return `${value}°${unit}`
  }
  // One decimal of precision matches the schema's Decimal(5,1).
  return `${num.toFixed(1)}°${unit}`
}

/**
 * Absolute short timestamp ("Jul 20, 2:41 PM") for provenance lines —
 * signatures, temp readings, timeline steps. Compliance provenance wants
 * absolute times, not relative phrasing. Returns the input on bad data.
 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** Browser print view for one record — the canonical export path. */
export const productionRecordPrintHref = (recordId: string): string =>
  `/production/${encodeURIComponent(recordId)}/print`
